import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Layout, Menu, Result, Typography, theme } from "antd";
import {
  AlertOutlined,
  AppstoreOutlined,
  AuditOutlined,
  CloudServerOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  InboxOutlined,
  ScanOutlined,
  HistoryOutlined,
  OrderedListOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { ROLE_LABEL } from "@shoe-trace/shared";
import { fetchHealth } from "../api/client";
import { canManage, useSession } from "../session";

const { Header, Sider, Content } = Layout;

/* 对齐 Layout「固定侧边栏」示例的粘性侧栏，但滚动放在菜单层：
   Sider 自身 overflow:auto 会把零宽触发器裁掉，也会把品牌标题一起滚走。 */
const siderStyle: CSSProperties = {
  height: "100%",
  minHeight: 0,
  position: "sticky",
  insetInlineStart: 0,
  top: 0,
  bottom: 0,
};

function resetScroll(node: Element | null): void {
  if (node && node.scrollTop !== 0) node.scrollTop = 0;
}

export function AppLayout(): JSX.Element {
  const { token } = theme.useToken();
  const { user, expired, logout, dismissExpired } = useSession();
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [collapsed, setCollapsed] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [online, setOnline] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useLayoutEffect(() => {
    resetScroll(document.scrollingElement);
    resetScroll(contentRef.current);
    let parent = contentRef.current?.parentElement ?? null;
    while (parent) {
      resetScroll(parent);
      parent = parent.parentElement;
    }
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const ping = () => {
      void fetchHealth()
        .then(() => {
          if (!cancelled) setOnline(true);
        })
        .catch(() => {
          if (!cancelled) setOnline(false);
        });
    };
    ping();
    const timer = window.setInterval(ping, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const items: MenuProps["items"] = [
    {
      type: "group",
      label: "今天的工作",
      children: [
        { key: "/scan", icon: <ScanOutlined />, label: "扫码工作台" },
        { key: "/", icon: <DashboardOutlined />, label: "总览" },
        { key: "/stock", icon: <InboxOutlined />, label: "库存" },
        { key: "/skus", icon: <AppstoreOutlined />, label: "SKU 建档" },
        { key: "/tag-station", icon: <TagsOutlined />, label: "赋码打印" },
        { key: "/locations", icon: <EnvironmentOutlined />, label: "库位" },
        { key: "/picking", icon: <OrderedListOutlined />, label: "拣货指引" },
        { key: "/packing", icon: <ThunderboltOutlined />, label: "出库复核" },
        { key: "/stocktake", icon: <AuditOutlined />, label: "盘点与调整" },
      ],
    },
    {
      type: "group",
      label: "查询与异常",
      children: [
        { key: "/timeline", icon: <HistoryOutlined />, label: "时光轴" },
        { key: "/exceptions", icon: <AlertOutlined />, label: "异常中心" },
        ...(canManage(user) ? [{ key: "/reconciliation", icon: <FileSearchOutlined />, label: "对账记录" }] : []),
      ],
    },
    ...(user?.role === "ADMIN"
      ? [
          {
            type: "group" as const,
            label: "系统",
            children: [{ key: "/accounts", icon: <TeamOutlined />, label: "账号管理" }],
          },
        ]
      : []),
  ];

  const selected = location.pathname === "/" ? "/" : location.pathname;

  if (expired) {
    return (
      <div className="app-scroll-page app-scroll-page--plain">
        <Result
          status="info"
          title="登录已过期"
          subTitle="请重新登录，然后从左侧菜单继续刚才的工作。"
          extra={
            <Button
              type="primary"
              onClick={() => {
                dismissExpired();
                navigate("/login");
              }}
            >
              重新登录
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <Layout hasSider className="app-shell">
      <Sider
        theme="dark"
        collapsible
        breakpoint="md"
        width={220}
        collapsedWidth={narrow ? 0 : 80}
        collapsed={collapsed}
        zeroWidthTriggerStyle={narrow ? { top: 4 } : undefined}
        onBreakpoint={(broken) => {
          setNarrow(broken);
          setCollapsed(broken);
        }}
        onCollapse={(next) => setCollapsed(next)}
        style={siderStyle}
      >
        <div className="app-sider-brand" style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", fontSize: 16 }}>
          {collapsed ? "万昌" : "万昌 WMS"}
        </div>
        <div className="app-sider-scroll">
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selected]}
            items={items}
            onClick={({ key }) => {
              navigate(key);
              if (window.innerWidth < 768) setCollapsed(true);
            }}
          />
        </div>
      </Sider>
      <Layout className="app-main">
        <Header
          className="app-header no-print"
          style={{
            background: "#fff",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: "flex",
            alignItems: "center",
            gap: narrow ? 8 : 16,
            padding: narrow ? "0 8px 0 48px" : "0 24px",
          }}
        >
          <Typography.Title level={4} style={{ margin: 0, flex: 1 }}>
            <span className="app-header__title-full">万昌 WMS · 仓库工作台</span>
            <span className="app-header__title-short">万昌 WMS</span>
          </Typography.Title>
          <Typography.Text type="secondary">
            {user ? `${user.name}（${ROLE_LABEL[user.role]}）` : ""}
          </Typography.Text>
          <Badge
            status={online ? "success" : "error"}
            text={
              <span>
                <CloudServerOutlined /> {online ? "系统已连上" : "系统没连上"}
              </span>
            }
          />
          <Button
            type="link"
            onClick={() => {
              void logout().then(() => navigate("/login"));
            }}
          >
            退出并换人登录
          </Button>
        </Header>
        <Content ref={contentRef} className="app-content" style={{ margin: location.pathname === "/scan" ? 8 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
